import { fetchMyBuilds } from '@/api/builds';
import { fetchSenderProfileSettings, updateSenderProfileSettings } from '@/api/sender-panel';
import LoadingWrapper from '@/components/loading-wrapper';
import { Button } from '@/components/ui/button';
import { InputGroup, InputGroupTextarea } from '@/components/ui/input-group';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import type { IBuild } from '@/entities/build.entity';
import { $features } from '@/entities/features-entity';
import { senderInlineFactClass } from '@/pages/sender/components/sender-surface';
import SenderBuildPicker from '@/pages/sender/components/sender-build-picker';
import { SenderScreen, SENDER_ADMIN_DISABLED, senderPrimaryButtonClassName } from '@/pages/sender/sender-screen';
import { useUnit } from 'effector-react';
import { resolveSenderActionError } from '@/lib/resolve-sender-action-error';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

export default function SenderSettingsPage() {
	const { senderHubProfileSettingsVisible } = useUnit($features);
	const [builds, setBuilds] = useState<IBuild[]>([]);
	const [buildId, setBuildId] = useState('');
	const [deviceBuildId, setDeviceBuildId] = useState('');
	const [caption, setCaption] = useState('');
	const [savedBuildId, setSavedBuildId] = useState('');
	const [savedCaption, setSavedCaption] = useState('');
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const [settings, buildList] = await Promise.all([fetchSenderProfileSettings(), fetchMyBuilds()]);
			const ready = buildList.filter(build => build.hasApk && build.successBuilded);
			const nextBuild = settings.landingDefaultBuildId ?? '';
			const nextCaption = settings.autoRunDefaultCaption ?? '';
			setBuilds(ready);
			setBuildId(nextBuild);
			setDeviceBuildId(settings.deviceDefaultBuildId ?? '');
			setCaption(nextCaption);
			setSavedBuildId(nextBuild);
			setSavedCaption(nextCaption);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		if (!senderHubProfileSettingsVisible) {
			setLoading(false);
			return;
		}
		void load();
	}, [load, senderHubProfileSettingsVisible]);

	const dirty = buildId !== savedBuildId || caption !== savedCaption;

	async function save() {
		setSaving(true);
		try {
			await updateSenderProfileSettings({
				landingDefaultBuildId: buildId || null,
				deviceDefaultBuildId: deviceBuildId || null,
				autoRunDefaultCaption: caption.trim(),
			});
			setSavedBuildId(buildId);
			setSavedCaption(caption);
			toast.success('Сохранено');
		} catch (error) {
			toast.error(resolveSenderActionError(error, 'Не удалось сохранить'));
		} finally {
			setSaving(false);
		}
	}

	if (!senderHubProfileSettingsVisible) {
		return (
			<SenderScreen title='Настройки лендинга'>
				<p className='text-[15px]'>{SENDER_ADMIN_DISABLED}</p>
			</SenderScreen>
		);
	}

	return (
		<SenderScreen
			title='Настройки лендинга'
			footer={
				<div className='flex flex-col gap-2'>
					<LoadingWrapper show={saving}>
						<Button type='button' className={senderPrimaryButtonClassName()} disabled={!dirty || saving || loading} onClick={() => void save()}>
							Сохранить
						</Button>
					</LoadingWrapper>
					<button
						type='button'
						className='min-h-11 text-[15px] text-primary'
						onClick={() => toast.message('Смените PIN в основном боте')}
					>
						Сменить PIN
					</button>
				</div>
			}
		>
			<p className={senderInlineFactClass + ' text-[15px]'}>
				Ручной запуск берёт сценарий. Здесь — билд и подпись для входа с лендинга.
			</p>
			{loading ? (
				<div className='flex justify-center py-8'>
					<Spinner />
				</div>
			) : (
				<div className='flex flex-col gap-3'>
					<SenderBuildPicker builds={builds} buildId={buildId} onBuildIdChange={setBuildId} />
					<Label htmlFor='landing-caption'>Подпись</Label>
					<InputGroup>
						<InputGroupTextarea
							id='landing-caption'
							value={caption}
							onChange={event => setCaption(event.target.value)}
							maxLength={1024}
							rows={4}
						/>
					</InputGroup>
				</div>
			)}
		</SenderScreen>
	);
}
